import React, { useEffect, useState } from 'react';
import { CheckCircle2, Pencil, ExternalLink, Save, SkipForward, X } from 'lucide-react';
import type { FeedbackChatModifier } from '../../hooks/useAdminApi';

interface FeedbackModifierCardProps {
  modifier: FeedbackChatModifier;
  isDarkMode: boolean;
  isBusy?: boolean;
  onAccept: () => void;
  onEdit: (nextText: string) => void;
  onSkip?: () => void;
  title?: string;
  acceptedLinkHref?: string;
  acceptedLinkLabel?: string;
  skipLabel?: string;
}

const FeedbackModifierCard: React.FC<FeedbackModifierCardProps> = ({
  modifier,
  isDarkMode,
  isBusy = false,
  onAccept,
  onEdit,
  onSkip,
  title = 'Modifier',
  acceptedLinkHref = '/admin?tab=behavior',
  acceptedLinkLabel = 'Im Admin öffnen',
  skipLabel = 'Überspringen',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState(modifier.text);

  useEffect(() => {
    setDraftText(modifier.text);
    if (modifier.status === 'accepted') {
      setIsEditing(false);
    }
  }, [modifier.text, modifier.status]);

  const cardClasses = isDarkMode
    ? 'border-gray-700 bg-gray-950/80 text-gray-100'
    : 'border-gray-200 bg-gray-50 text-gray-900';

  const statusBadgeClasses = modifier.status === 'accepted'
    ? isDarkMode
      ? 'bg-emerald-900/40 text-emerald-300'
      : 'bg-emerald-100 text-emerald-700'
    : isDarkMode
      ? 'bg-blue-900/40 text-blue-300'
      : 'bg-blue-100 text-blue-700';

  return (
    <div className={`mt-3 rounded-xl border ${cardClasses}`}>
      <div className={`flex items-center justify-between gap-3 border-b px-4 py-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-[0.12em]">{title}</span>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusBadgeClasses}`}>
            {modifier.status === 'accepted' ? 'Angenommen' : 'Entwurf'}
          </span>
        </div>
        {modifier.status === 'accepted' ? (
          <a
            href={acceptedLinkHref}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 text-xs underline ${isDarkMode ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-700'}`}
          >
            {acceptedLinkLabel}
            <ExternalLink size={12} />
          </a>
        ) : null}
      </div>

      <div className="px-4 py-3">
        {isEditing && modifier.status !== 'accepted' ? (
          <>
            <textarea
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              rows={6}
              className={`w-full resize-y rounded-lg border px-3 py-2 text-sm ${
                isDarkMode
                  ? 'border-gray-700 bg-gray-900 text-white'
                  : 'border-gray-300 bg-white text-gray-900'
              }`}
            />
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  onEdit(draftText);
                  setIsEditing(false);
                }}
                disabled={isBusy || !draftText.trim()}
                className="inline-flex items-center gap-1 rounded-lg bg-blue-500 px-3 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50"
              >
                <Save size={14} />
                Speichern
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraftText(modifier.text);
                  setIsEditing(false);
                }}
                className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium ${
                  isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <X size={14} />
                Abbrechen
              </button>
            </div>
          </>
        ) : (
          <>
            <pre className={`overflow-x-auto whitespace-pre-wrap rounded-lg border px-3 py-3 text-sm leading-relaxed ${
              isDarkMode ? 'border-gray-700 bg-gray-900 text-gray-100' : 'border-gray-200 bg-white text-gray-900'
            }`}>
              {modifier.text}
            </pre>
            {modifier.status !== 'accepted' ? (
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={onAccept}
                  disabled={isBusy}
                  className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
                >
                  <CheckCircle2 size={14} />
                  Annehmen
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  disabled={isBusy}
                  className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium ${
                    isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Pencil size={14} />
                  Editieren
                </button>
                {onSkip ? (
                  <button
                    type="button"
                    onClick={onSkip}
                    disabled={isBusy}
                    className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium ${
                      isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <SkipForward size={14} />
                    {skipLabel}
                  </button>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default FeedbackModifierCard;
